export const LENGTH_TYPE = "length";
export const RANGE_TYPE = "range";
export const SIZE_MB_TYPE = "sizeMB";

export const LENGTH = "length";
export const PROPS = "props";

export const VALIDATOR_TYPE = "validatorType";
export const MIN_VALUE = "minValue";
export const MAX_VALUE = "maxValue";
export const PRECISION = "precision";

export const IS_OK = "isOk";
export const PREVENT_UPDATE = "preventUpdate";
export const LENGTH_NOT_VALID = "lengthNotValid";
export const PRECISION_NOT_VALID = "precisionNotValid";

const LEFT = "left";
const CURRENT_LENGTH = "currentLength";

const INT_NUMBER_REGEX = /[0-9]/;
const NUMBER_REGEX = /[0-9.,]/;
const SPECIAL_CHARS = [
  "Backspace",
  "Tab",
  "End",
  "Home",
  "Delete",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Meta"
];

export default class ValidatorUtils {
  static lengthValidator(length) {
    return {
      [VALIDATOR_TYPE]: LENGTH_TYPE,
      [PROPS]: {
        [LENGTH]: length
      }
    };
  }

  static numberRangeValidator(minValue, maxValue, precision) {
    return {
      [VALIDATOR_TYPE]: RANGE_TYPE,
      [PROPS]: {
        [MIN_VALUE]: minValue,
        [MAX_VALUE]: maxValue,
        [PRECISION]: precision
      }
    };
  }

  static sizeMbValidator(maxValue) {
    return {
      [VALIDATOR_TYPE]: SIZE_MB_TYPE,
      [PROPS]: {
        [MAX_VALUE]: maxValue
      }
    };
  }

  static createValidator(type) {
    let result;
    if (type === LENGTH_TYPE) {
      result = ValidatorUtils.lengthValidator();
    } else if (type === RANGE_TYPE) {
      result = ValidatorUtils.numberRangeValidator();
    } else if (type === SIZE_MB_TYPE) {
      result = ValidatorUtils.sizeMbValidator();
    }
    return result;
  }

  static validate(value, validator) {
    let result = {};
    if (validator[VALIDATOR_TYPE] === LENGTH_TYPE) {
      result = ValidatorUtils.validateLenght(value, validator.props.length);
    } else if (validator[VALIDATOR_TYPE] === RANGE_TYPE) {
      let numValue = value === null || value === undefined || value === "" ? null : Number(value.replace(",", "."));
      result = ValidatorUtils.validateRange(
        numValue,
        Number(validator.props[MIN_VALUE]),
        Number(validator.props[MAX_VALUE]),
        Number(validator.props[PRECISION]) >= 0 ? validator.props[PRECISION] : 0
      );
    } else if (validator[VALIDATOR_TYPE] === SIZE_MB_TYPE) {
      result = ValidatorUtils.validateSize(value, validator.props[MAX_VALUE]);
    }
    return result;
  }

  static validateLenght(value, maxLength) {
    const currLength = ValidatorUtils.getTextLength(value);
    const hasMaxLength =
      maxLength !== null && maxLength !== undefined && String(maxLength).trim() !== "";
    const numericMaxLength = Number(maxLength);

    if (!hasMaxLength || !Number.isFinite(numericMaxLength) || numericMaxLength < 0) {
      return null;
    }

    return {
      [IS_OK]: currLength <= numericMaxLength,
      [LEFT]: numericMaxLength - currLength,
      [LENGTH]: numericMaxLength,
      [CURRENT_LENGTH]: currLength,
      [PREVENT_UPDATE]: false //TODO: 14.04.2020: This is a workaround deactivating the length check for comment
      // questions
    };
  }

  static getTextLength(value) {
    const document = new DOMParser().parseFromString(value || "", "text/html");
    return (document.body.textContent || "").length;
  }

  static validateSize(files = [], maxSize) {
    let value = 0;
    let mbSize = 1024 * 1024;
    let isOk = true;
    for (let i = 0; i < files.length && isOk; i++) {
      value = files[i].size / mbSize;
      isOk = value <= maxSize;
    }
    return {
      [IS_OK]: isOk,
      [LENGTH]: maxSize,
      [PREVENT_UPDATE]: true
    };
  }

  static validateRange(numValue, minValue, maxValue, precision) {
    let result = null;
    if (numValue !== null) {
      minValue = Number.isNaN(minValue) ? Number.MIN_SAFE_VALUE : Number(minValue);
      maxValue = Number.isNaN(maxValue) ? Number.MAX_SAFE_VALUE : Number(maxValue);
      let fixedValue = !(Number.isNaN(numValue) || Number.isNaN(precision))
        ? Number(numValue.toFixed(precision))
        : numValue;
      let isRangeOk =
        !Number.isNaN(numValue) && (minValue ? numValue >= minValue : true) && (maxValue ? numValue <= maxValue : true);
      let isPrecisionOk = numValue === fixedValue;
      let preventRangeUpdate = numValue > maxValue;
      let rangeValidatingNotGood = numValue < minValue || numValue > maxValue;
      if (!isRangeOk) {
        result = {
          [IS_OK]: isRangeOk,
          [MIN_VALUE]: minValue,
          [MAX_VALUE]: maxValue,
          [PRECISION]: precision,
          [PREVENT_UPDATE]: preventRangeUpdate || !isPrecisionOk,
          [LENGTH_NOT_VALID]: rangeValidatingNotGood,
          [PRECISION_NOT_VALID]: !isPrecisionOk
        };
      } else if (!isPrecisionOk) {
        result = {
          [PRECISION]: precision,
          [PREVENT_UPDATE]: preventRangeUpdate || !isPrecisionOk,
          [LENGTH_NOT_VALID]: rangeValidatingNotGood,
          [PRECISION_NOT_VALID]: !isPrecisionOk
        };
      } else {
        result = {
          [IS_OK]: isRangeOk,
          [MIN_VALUE]: minValue,
          [MAX_VALUE]: maxValue,
          [PRECISION]: precision
        };
      }
    }
    return result;
  }

  static prepareValidationMessage(validatorModel, validationResult) {
    let result = null;
    if (validatorModel) {
      if (validatorModel[VALIDATOR_TYPE] === LENGTH_TYPE) {
        result = ValidatorUtils.lengthMessage(validatorModel, validationResult);
      } else if (validatorModel[VALIDATOR_TYPE] === RANGE_TYPE) {
        result = ValidatorUtils.rangeMessage(validatorModel, validationResult);
      } else if (validatorModel[VALIDATOR_TYPE] === SIZE_MB_TYPE) {
        result = ValidatorUtils.sizeMessage(validatorModel, validationResult);
      }
    }
    return result;
  }

  static lengthMessage(validatorModel, validationResult) {
    const configuredLength = validatorModel?.props?.length;
    if (
      configuredLength === null ||
      configuredLength === undefined ||
      String(configuredLength).trim() === "" ||
      !Number.isFinite(Number(configuredLength)) ||
      Number(configuredLength) < 0
    ) {
      return null;
    }

    let currLength = !validationResult ? 0 : validationResult[CURRENT_LENGTH];
    return {
      format: { id: "app.validation.lengthMessage" },
      values: {
        [CURRENT_LENGTH]: currLength,
        length: !validationResult ? validatorModel.props.length : validationResult.length
      }
    };
  }

  static sizeMessage(validatorModel, validationResult) {
    return {
      format: { id: "app.validation.sizeMessage" },
      values: { length: validationResult ? validationResult[LENGTH] : "" }
    };
  }

  static rangeMessage(validatorModel, validationResult) {
    let result = null;
    if (validationResult && !validationResult[IS_OK]) {
      result = {
        format: {
          id: validationResult[LENGTH_NOT_VALID]
            ? "app.validation.rangeMessage"
            : validationResult[PRECISION_NOT_VALID]
            ? "app.validation.decimalMessage"
            : null
        },
        values: {
          [MIN_VALUE]: validationResult[MIN_VALUE],
          [MAX_VALUE]: validationResult[MAX_VALUE],
          [PRECISION]: validationResult[PRECISION]
        }
      };
    }
    return result;
  }

  static handleNumberInput(event) {
    if (
      !(
        event.metaKey ||
        event.shiftKey ||
        event.ctrlKey ||
        event.altKey ||
        NUMBER_REGEX.test(event.key) ||
        SPECIAL_CHARS.indexOf(event.key) >= 0
      )
    ) {
      event.preventDefault();
    }
  }

  static handleIntNumberInput(event) {
    if (
      !(
        event.metaKey ||
        event.shiftKey ||
        event.ctrlKey ||
        event.altKey ||
        INT_NUMBER_REGEX.test(event.key) ||
        SPECIAL_CHARS.indexOf(event.key) >= 0
      )
    ) {
      event.preventDefault();
    }
  }

  static formatNumberInput(intl, stringNumber) {
    if (!ValidatorUtils.isNumber(intl, stringNumber)) {
      return "";
    }

    let decimalSeparator = ValidatorUtils.getLocaleNumberFractionSeparator(intl);

    return intl.formatNumber(parseFloat(stringNumber.replace(new RegExp("[" + decimalSeparator + "]", "g"), ".")), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 10,
      useGrouping: false
    });
  }

  static isNumber(intl, stringNumber) {
    const decimalSeparator = ValidatorUtils.getLocaleNumberFractionSeparator(intl);
    const fullRegex = new RegExp("^[0-9]*[" + decimalSeparator + "]?[0-9]*$");

    const matched = fullRegex.test(stringNumber);

    return matched && !isNaN(parseFloat(stringNumber.replace(new RegExp(`[${decimalSeparator}]`, "g"), ".")));
  }

  static getLocaleNumberFractionSeparator(intl) {
    return intl.formatNumberToParts(1.1, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1
    })[1].value;
  }

  static getIntlNumberInput(text, intl) {
    const decimalSeparator = ValidatorUtils.getLocaleNumberFractionSeparator(intl);
    return Number(text.replace(decimalSeparator, ".")).toLocaleString(intl.locale);
  }

  static getStandardNumberInput(text, intl) {
    return text.replaceAll(ValidatorUtils.getNumberGroupSeparator(intl), "");
  }

  static getNumberGroupSeparator(intl) {
    const parts = new Intl.NumberFormat(intl.locale).formatToParts(1000);
    return parts.find(part => part.type === 'group').value;
  }
}
